import { Accordion as BaseAccordion } from "@base-ui/react/accordion";
import plusIcon from "@loot-os/icons/icons/plus";
import type { ComponentProps, ReactNode } from "react";

import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { Icon } from "./Icon.js";

/** A stack of questions; `multiple` lets several stay open, `defaultValue` opens some. */
export function Accordion({
  className,
  ...props
}: Omit<ComponentProps<typeof BaseAccordion.Root>, "className"> & { className?: string }) {
  return <BaseAccordion.Root className={cn("flex flex-col", className)} {...props} />;
}

export interface AccordionItemProps
  extends Omit<ComponentProps<typeof BaseAccordion.Item>, "className" | "children"> {
  question: string;
  /** Say the answer; a link alone isn't one. */
  answer: ReactNode;
  className?: string;
}

/**
 * One question and its answer. The plus turns to a cross as the answer fades down; only
 * transform and opacity animate, so the rows below move at once.
 */
export function AccordionItem({ question, answer, className, ...props }: AccordionItemProps) {
  return (
    <BaseAccordion.Item className={cn("border-b border-border-subtle", className)} {...props}>
      <BaseAccordion.Header>
        <BaseAccordion.Trigger
          className={cn(
            "group flex w-full cursor-pointer items-center justify-between gap-4 rounded-sm py-5 text-left font-body text-heading-h3 text-text-primary hover:text-accent-text [-webkit-tap-highlight-color:transparent]",
            FOCUS_RING,
          )}
        >
          {question}
          {/* The wrapper turns, not the <svg>: browsers won't composite an SVG transform. It turns
              only on the way open: a closing row snaps back to a plus, so when one row opens as
              another closes, only one ever reads as open. */}
          <span className="grid size-8 shrink-0 place-items-center rounded-pill bg-bg-raised text-text-secondary group-hover:bg-bg-hover group-hover:text-text-primary group-active:bg-bg-pressed group-data-panel-open:rotate-45 group-data-panel-open:text-accent-text group-data-panel-open:transition-transform group-data-panel-open:duration-base group-data-panel-open:ease-standard motion-reduce:transition-none">
            <Icon icon={plusIcon} />
          </span>
        </BaseAccordion.Trigger>
      </BaseAccordion.Header>
      {/* A closing answer leaves the layout the moment its row closes (`data-closed`), not after
          Base UI's exit check a few frames on: otherwise, opening one row while another closes
          shows both open for a frame. */}
      <BaseAccordion.Panel className="pb-6 font-body text-body-b1 text-text-secondary data-closed:hidden motion-safe:transition-[opacity,translate] motion-safe:duration-slow motion-safe:ease-enter motion-safe:data-starting-style:-translate-y-2 motion-safe:data-starting-style:opacity-0">
        <div className="max-w-prose">{answer}</div>
      </BaseAccordion.Panel>
    </BaseAccordion.Item>
  );
}
